import { useEffect, useCallback } from 'react';
import type { Socket } from './socket';
import { socketManager } from './socket';
import {
  SOCKET_EVENTS,
  SocketEventMap,
  IncomingCallPayload,
  CallAnsweredPayload,
} from './events';
import { useVoiceStore } from '../../stores/voiceStore';
import { useAuthStore } from '../../stores/authStore';
import { voiceService } from '../../features/voice/services/voiceService';
import { twilioClient } from '../../features/voice/twilio-client';

/**
 * Hook to get the socket instance
 */
export function useSocket(): Socket | null {
  return socketManager.getSocket();
}

/**
 * Hook to subscribe to a socket event
 */
export function useSocketEvent<K extends keyof SocketEventMap>(
  event: K,
  handler: (data: SocketEventMap[K]) => void
): void {
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [socket, event, handler]);
}

/**
 * Hook to emit socket events
 */
export function useSocketEmit() {
  const socket = useSocket();

  return useCallback(
    <K extends keyof SocketEventMap>(event: K, data?: SocketEventMap[K]) => {
      if (!socket || !socket.connected) {
        console.warn('Socket not connected, cannot emit event:', event);
        return;
      }

      socket.emit(event, data);
    },
    [socket]
  );
}

/**
 * Hook to subscribe to a conversation.
 * Subscribes immediately if connected, and re-subscribes on reconnect
 * (server-side room membership is lost when the socket reconnects).
 */
export function useConversationSubscription(conversationId: string | null) {
  const socket = useSocket();

  useEffect(() => {
    if (!conversationId || !socket) return;

    const subscribe = () => {
      socket.emit(SOCKET_EVENTS.SUBSCRIBE_CONVERSATION, conversationId);
    };

    if (socket.connected) {
      subscribe();
    }

    socket.on('connect', subscribe);

    return () => {
      socket.off('connect', subscribe);
      if (socket.connected) {
        socket.emit(SOCKET_EVENTS.UNSUBSCRIBE_CONVERSATION, conversationId);
      }
    };
  }, [conversationId, socket]);
}

/**
 * Hook to subscribe to a team group-chat channel room.
 * Mirrors useConversationSubscription — subscribes when connected and
 * re-subscribes on reconnect (server room membership is lost on reconnect).
 */
export function useTeamChannelSubscription(channelId: string | null) {
  const socket = useSocket();

  useEffect(() => {
    if (!channelId || !socket) return;

    const subscribe = () => {
      socket.emit(SOCKET_EVENTS.SUBSCRIBE_TEAM_CHANNEL, channelId);
    };

    if (socket.connected) {
      subscribe();
    }

    socket.on('connect', subscribe);

    return () => {
      socket.off('connect', subscribe);
      if (socket.connected) {
        socket.emit(SOCKET_EVENTS.UNSUBSCRIBE_TEAM_CHANNEL, channelId);
      }
    };
  }, [channelId, socket]);
}

/**
 * Bridges voice-call socket events into the voiceStore.
 *
 * Mount once at the app root. Listens for `call:incoming` and `call:answered`
 * on the default namespace and updates the store accordingly:
 *   - `call:incoming`     → setIncoming
 *   - `call:answered`     → if the answering staff isn't us, clearIncoming
 *                           (keeps our toast visible until WE click Answer)
 */
export function useVoiceSocketBridge(): void {
  // Re-evaluate when authentication changes — the socket is created on login.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const socket = useSocket();

  useEffect(() => {
    if (!isAuthenticated || !socket) return;

    const onIncoming = (payload: IncomingCallPayload) => {
      // Ignore further rings if we're already on a call.
      if (useVoiceStore.getState().activeCall) return;
      useVoiceStore.getState().setIncoming(payload);
    };

    const onAnswered = (payload: CallAnsweredPayload) => {
      const { incomingCall, activeCall } = useVoiceStore.getState();
      const myStaffId = useAuthStore.getState().user?.id;

      // If someone else answered the call we were ringing for, dismiss our toast.
      if (
        incomingCall &&
        incomingCall.id === payload.callId &&
        payload.staffId !== myStaffId
      ) {
        useVoiceStore.getState().clearIncoming();
      }

      // If somehow we have an active call that another staff also "answered",
      // it's a server-side first-wins race we lost — drop our local state.
      if (
        activeCall &&
        activeCall.call.id === payload.callId &&
        payload.staffId !== myStaffId
      ) {
        if (activeCall.transport === 'webrtc') {
          try {
            activeCall.pc?.close();
          } catch {
            /* ignore */
          }
          activeCall.localStream?.getTracks().forEach((t) => t.stop());
        } else if (activeCall.transport === 'twilio') {
          twilioClient.endCall();
        }
        useVoiceStore.getState().clearActive();
      }
    };

    socket.on(SOCKET_EVENTS.CALL_INCOMING, onIncoming);
    socket.on(SOCKET_EVENTS.CALL_ANSWERED, onAnswered);

    return () => {
      socket.off(SOCKET_EVENTS.CALL_INCOMING, onIncoming);
      socket.off(SOCKET_EVENTS.CALL_ANSWERED, onAnswered);
    };
  }, [socket, isAuthenticated]);
}

/**
 * Bootstraps the Twilio Voice JS device when the store is configured for the
 * Twilio transport AND the staff user is available for calls.
 *
 * Lifecycle:
 *   - On mount (and when auth/availability/transport changes), fetch the
 *     store's voice settings. If `transport === 'twilio'` and the user is
 *     `availableForCalls`, fetch a JWT and connect the Twilio Device.
 *   - When the user toggles `availableForCalls` off, tear the device down
 *     so the browser stops receiving rings. Any in-flight call is also
 *     dropped — this matches the WebRTC path's "go offline → end call".
 *   - When the transport is `webrtc`, never bootstrap the device. Keeps
 *     unused infra silent.
 *
 * Incoming-call routing: when the Twilio Device fires `incoming`, we surface
 * it through the same `voiceStore.incomingCall` channel as the WebRTC path
 * so `IncomingCallToast` doesn't need to know which transport was used to
 * decide whether to render.
 */
export function useTwilioDeviceBootstrap(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const availableForCalls = useVoiceStore((s) => s.availableForCalls);

  useEffect(() => {
    if (!isAuthenticated) {
      twilioClient.disconnect();
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const settings = await voiceService.getSettings();
        if (cancelled) return;
        if (settings.transport !== 'twilio' || !availableForCalls) {
          // Wrong transport, or user opted out — make sure we're torn down.
          twilioClient.disconnect();
          return;
        }
        const tokenInfo = await voiceService.getTwilioToken();
        if (cancelled) return;
        await twilioClient.connect(tokenInfo.token, (payload) => {
          // Mirror the WebRTC `call:incoming` flow: drop the payload into the
          // store and let `IncomingCallToast` render. Skip if we're already
          // on a call (matches the existing socket-bridge guard).
          if (useVoiceStore.getState().activeCall) return;
          useVoiceStore.getState().setIncoming(payload);
        });
      } catch (err) {
        // Settings/token fetch can fail if the backend hasn't shipped Phase
        // 4B yet. Don't crash the app — just leave Twilio disconnected.
        console.warn('[voice] failed to bootstrap Twilio device:', err);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, availableForCalls]);

  // Tear the device down on full unmount (e.g., page nav between SPA roots).
  useEffect(() => {
    return () => {
      twilioClient.disconnect();
    };
  }, []);
}

/**
 * Hook to handle typing indicators
 */
export function useTypingIndicator(conversationId: string | null) {
  const emit = useSocketEmit();
  const socket = useSocket();

  const startTyping = useCallback(() => {
    if (!conversationId || !socket?.connected) return;
    emit(SOCKET_EVENTS.TYPING_START, conversationId);
  }, [conversationId, socket, emit]);

  const stopTyping = useCallback(() => {
    if (!conversationId || !socket?.connected) return;
    emit(SOCKET_EVENTS.TYPING_STOP, conversationId);
  }, [conversationId, socket, emit]);

  return { startTyping, stopTyping };
}
